import React from 'react';
import type {
    ImageCropUploadValue,
    ImageUploadPayload,
} from '@/components/imageCropUpload';
import { useResetOnKeyChange } from '@/utils';

export type SupplierFormInitialValues = {
    name: string;
    serialNumber: string;
    phone: string;
    email: string;
    image: ImageCropUploadValue | null;
};

/**
 * What the modal hands back when the user submits. `imageUrl` is the
 * *existing* URL on edits (or `null` when cleared); `imageUpload` is only set
 * when the user picked a fresh image that still needs to be uploaded.
 *
 * The optional text fields are emitted as `null` when blank so the wire
 * payload matches the bos-api's "blank means null" contract.
 */
export type SupplierFormSubmit = {
    name: string;
    serialNumber: string | null;
    phone: string | null;
    email: string | null;
    imageUpload: ImageUploadPayload | null;
    imageUrl: string | null;
};

type UseSupplierFormParams = {
    initialValues?: SupplierFormInitialValues;
    isOpen: boolean;
    onSubmit: (input: SupplierFormSubmit) => void;
};

const EMPTY_INITIAL: SupplierFormInitialValues = {
    name: '',
    serialNumber: '',
    phone: '',
    email: '',
    image: null,
};

/**
 * Lightweight client-side shape check (`local@host.tld`) used to gate the
 * submit button and surface an inline error before the round-trip. This is
 * a UX guard only — it is intentionally stricter than the bos-api's
 * `EmailAddressAttribute` (which doesn't require a dot in the host), and
 * the server remains the source of truth on accept/reject.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isEmailValid = (email: string): boolean =>
    EMAIL_PATTERN.test(email.trim());

const nullIfBlank = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
};

/**
 * Form state for the Add / Edit Supplier modal. Mirrors `useCategoryForm`:
 * re-seed from `initialValues` on every open and close, gate submit on the
 * required fields, and carry the cropper payload through so the page hook can
 * drive the upload.
 */
export const useSupplierForm = ({
    initialValues,
    isOpen,
    onSubmit,
}: UseSupplierFormParams) => {
    const initial = initialValues ?? EMPTY_INITIAL;

    const [image, setImage] = React.useState<ImageCropUploadValue | null>(
        initial.image,
    );
    const [name, setName] = React.useState(initial.name);
    const [serialNumber, setSerialNumber] = React.useState(
        initial.serialNumber,
    );
    const [phone, setPhone] = React.useState(initial.phone);
    const [email, setEmail] = React.useState(initial.email);
    const [emailTouched, setEmailTouched] = React.useState(false);

    // Re-seed every field from initialValues on each open/close transition —
    // see useCategoryForm. The modal is always mounted and the page opens Edit
    // and sets the record in one update, so seeding on open is what fills the
    // supplier's values; seeding on close clears an Add draft. Keyed on
    // `isOpen` only, so a background refetch mid-edit doesn't clobber the
    // user's typing.
    useResetOnKeyChange(isOpen, () => {
        setImage(initial.image);
        setName(initial.name);
        setSerialNumber(initial.serialNumber);
        setPhone(initial.phone);
        setEmail(initial.email);
        setEmailTouched(false);
    });

    const onNameChange = React.useCallback((value: string) => {
        setName(value);
    }, []);

    const onSerialNumberChange = React.useCallback((value: string) => {
        setSerialNumber(value);
    }, []);

    const onPhoneChange = React.useCallback((value: string) => {
        setPhone(value);
    }, []);

    const onEmailChange = React.useCallback((value: string) => {
        setEmail(value);
        setEmailTouched(true);
    }, []);

    const onEmailBlur = React.useCallback(() => {
        setEmailTouched(true);
    }, []);

    const onImageChange = React.useCallback(
        (value: ImageCropUploadValue | null) => {
            setImage(value);
        },
        [],
    );

    const trimmedName = name.trim();
    const emailIsBlank = email.trim().length === 0;
    const emailLooksValid = emailIsBlank || isEmailValid(email);
    const isValid = trimmedName.length > 0 && emailLooksValid;
    // Surface the inline error only once the user has tried to type into the
    // field — typing fresh shouldn't show the "invalid" state mid-keystroke.
    const showEmailError =
        emailTouched && !emailIsBlank && !isEmailValid(email);

    // We deliberately do NOT close the modal here — the parent's submit
    // handler owns the close decision (close on success, keep open on
    // failure so the inline `formError` is visible and the user can retry
    // without re-entering everything).
    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            name: trimmedName,
            serialNumber: nullIfBlank(serialNumber),
            phone: nullIfBlank(phone),
            email: nullIfBlank(email),
            imageUpload: image?.imageUpload ?? null,
            imageUrl: image?.imageUpload ? null : (image?.imageUrl ?? null),
        });
    }, [email, image, isValid, onSubmit, phone, serialNumber, trimmedName]);

    return {
        image,
        isValid,
        name,
        serialNumber,
        phone,
        email,
        showEmailError,
        onImageChange,
        onNameChange,
        onSerialNumberChange,
        onPhoneChange,
        onEmailChange,
        onEmailBlur,
        onSubmit: onSubmitClick,
    };
};
